"use client";

import type {ApprovalDecisionOutcome} from "@/types/chatMessages";
import {useEffect, useState} from "react";
import {readMutationExecution} from "@repo/ui/gailgpt/mutation-execution";
import {outcomeNotice, resolveToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import {useToolApprovalDecisionController} from "@/contexts/chat/ToolApprovalDecisionContext";
import type {MutationProposal} from "@/services/wingman/mutation-proposal";
import {approvalCanBeApplied} from "@/services/wingman/tool-approval";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import {approvalPresentation} from "./approvalPresentation";

/**
 * One decision per approval: duplicate clicks, expired proposals and settled calls cannot submit.
 * `onRespond` records the decision for the reply, which submits its approvals together.
 */
export function useToolApprovalDecision({call, completed, result, active, disabled, onRespond, onRequestFresh, preview}: {
   call: MessageContentFunctionCall;
   preview?: ReturnType<typeof approvalPresentation>;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
}) {
   const approval = call.approval;
   const {state: {pending: action, submitting, requestedFresh, error, draft}, begin, finish, fail, markFreshRequested} = useToolApprovalDecisionController(approval?.id);
   // The reply submits every drafted decision together, including ones made on other surfaces.
   const draftedAction = draft ? "apply" : "discard";
   const pending = action ?? (submitting ? draftedAction : null);
   const [now, setNow] = useState(Date.now);
   const settled = completed || approval?.approved !== undefined;
   const expiresAt = approval?.proposal ? Date.parse(approval.proposal.expiresAt) : undefined;
   useEffect(() => {
      if (expiresAt === undefined || settled) return;
      const timer = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(timer);
   }, [expiresAt, settled]);

   const expired = expiresAt !== undefined && expiresAt <= now;
   const unavailable = (preview ?? approvalPresentation(call)).unavailable;
   const canRespond = !!approval && active && !settled && !disabled && !pending && !!onRespond;
   // A preview or presentation that could not be read must never fall back to approving unreviewed changes.
   const canApply = canRespond && approvalCanBeApplied(approval, now) && !unavailable;
   const status = resolveToolApprovalStatus({approval, completed, active, result, pending: !!pending});
   // Without a decision handler this viewer does not own the conversation.
   const ownerOnly = !!approval && active && !settled && !onRespond;
   // What Galactus saved, which can differ from the proposal; null when the receipt omitted it.
   const saved = status === "completed" && approval?.proposal ? readMutationExecution(result, approval.proposal)?.saved ?? null : null;
   // A decision held until the reply's other approvals are decided too.
   const queued = !settled && active && status === "required" ? draft : null;

   /** Reports whether the choice was queued, submitted, ignored, or failed. */
   const respond = async (approved: boolean): Promise<ApprovalDecisionOutcome | "failed"> => {
      if (!approval || !canRespond || !onRespond || (approved && (!approvalCanBeApplied(approval, Date.now()) || unavailable))) return "ignored";
      if (!begin(approved ? "apply" : "discard")) return "ignored";
      try {
         return await onRespond({id: approval.id, approved});
      } catch {
         fail();
         return "failed";
      } finally {
         finish();
      }
   };

   const canRequestFresh = !!approval?.proposal && !!onRespond && !!onRequestFresh && !disabled && !pending && !requestedFresh &&
      (!!outcomeNotice(status)?.requestFresh || (status === "denied" && expired) || (!settled && expired && active));
   const requestFresh = async () => {
      if (!canRequestFresh || !approval?.proposal || !onRequestFresh || !onRespond) return;
      // The fresh request only drafts composer text. A queued rejection is enough;
      // ignored or failed choices must not trigger that follow-up.
      if (!settled) {
         const outcome = await respond(false);
         if (outcome !== "queued" && outcome !== "submitted") return;
      }
      if (!begin("fresh")) return;
      try {
         await onRequestFresh(approval.proposal);
         markFreshRequested();
      } catch {
         fail();
      } finally {
         finish();
      }
   };

   return {
      canRequestFresh, requestFresh, requestedFresh, ownerOnly, saved, queued,
      status, settled, expired, error, pending, draft, canRespond, canApply, respond,
      remainingMs: expiresAt === undefined ? undefined : Math.max(0, expiresAt - now),
   };
}
